import { backupWarning } from "@/lib/backupWarning";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { newVersionAvailable } from "@/lib/newVersion";
import { useInstall } from "@/lib/install";
import { useAuth } from "@/context/AuthContext";
import api from "@/lib/api";
import { loadPlatformVersion } from "@/lib/version";
import { visibleGroups } from "@/lib/nav";

/** Starea comună a cojilor „moderne” (Fiord, Purple): contor de notificări, versiunea platformei, instalarea ca aplicație, bannerele (licență,
 *  pagină veche, actualizări) și meniul vizibil utilizatorului. Aceleași reguli ca în LayoutNavy; se schimbă doar prezentarea. */
export default function useShell() {
  const { user, logout, hasRole, hasModule, allowed, activationRequired } = useAuth();
  const nav = useNavigate();
  const [unread, setUnread] = useState(0);
  const [platformVersion, setPlatformVersion] = useState("");
  useEffect(() => { loadPlatformVersion().then(setPlatformVersion); }, []);
  const pwa = useInstall();
  const [iosHelp, setIosHelp] = useState(false);
  const [newBuild, setNewBuild] = useState(false); // pe server există o interfață mai nouă decât cea încărcată în browser
  useEffect(() => {
    const check = () => newVersionAvailable().then(v => { if (v) setNewBuild(true); });
    check();
    const t = setInterval(check, 5 * 60 * 1000);
    window.addEventListener("focus", check);
    return () => { clearInterval(t); window.removeEventListener("focus", check); };
  }, [user?.id]);
  const [isClientInstall, setIsClientInstall] = useState(false);
  const [licenseAlert, setLicenseAlert] = useState(null); // null | "inactive" | "locked"
  const [updateBanner, setUpdateBanner] = useState(null); // null | {kind: error|ok|info, text, link?}

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      try { const r = await api.get("/notifications?limit=1"); if (mounted) setUnread(r.data.unread || 0); } catch {}
    };
    if (activationRequired) return () => { mounted = false; };  // până la activare backend-ul refuză orice în afară de licență
    load();
    const t = setInterval(load, 30000);
    window.addEventListener("notifications-changed", load);   // marcare ca citit din pagina Notificări: contorul se actualizează imediat
    return () => { mounted = false; clearInterval(t); window.removeEventListener("notifications-changed", load); };
  }, [activationRequired]);

  // Pe o instalare de client, Adminul vede meniul „Licență" și o alertă dacă instalarea nu e activată.
  const isAdmin = hasRole("admin");
  useEffect(() => {
    const check = () => api.get("/config/deployment").then(r => {
      setIsClientInstall(!!r.data.is_client_install);
      if (r.data.is_client_install && isAdmin) {
        api.get("/activation/status").then(s => {
          setLicenseAlert(!s.data.activated ? "inactive" : s.data.locked ? "locked" : null);
        }).catch(() => {});
        // Starea actualizărilor, vizibilă pe orice pagină (inclusiv Panoul de control)
        if (!activationRequired) api.get("/updates/status").then(u => {
          const d = u.data || {};
          const recent = d.last_applied_at && (Date.now() - new Date(d.last_applied_at).getTime()) < 86400000;
          if (d.startup_error) setUpdateBanner({ kind: "error", text: "Actualizarea a întâmpinat o problemă la pornire. Contactează furnizorul." });
          else if (d.status === "failed") setUpdateBanner({ kind: "error", text: "Ultima actualizare a eșuat. Platforma rulează în continuare versiunea anterioară." });
          else if (d.update_stuck) setUpdateBanner({ kind: "error", text: "Actualizarea nu pornește: serviciul de actualizări de pe server nu răspunde (nu este instalat sau este oprit). Platforma rulează în continuare versiunea anterioară. Contactează administratorul serverului: pe server se rulează o dată `sudo ./kit/updater.sh`, apoi `sudo ./kit/setup-maintenance.sh`." });
          else if (d.status === "pending" || d.status === "downloading") setUpdateBanner({ kind: "info", text: "Actualizarea este în curs. Platforma repornește singură, în câteva minute." });
          else if (d.available) setUpdateBanner({ kind: "info", text: `Versiunea ${d.version} este disponibilă.`, link: "Preia update" });
          else if (d.status === "done" && recent) setUpdateBanner({ kind: "ok", text: `Actualizare aplicată: versiunea ${d.last_applied_version}. Totul funcționează normal.` });
          else if (backupWarning(d.backup_encryption)) setUpdateBanner({ kind: "warn", text: backupWarning(d.backup_encryption) });
          else setUpdateBanner(null);
        }).catch(() => {});
      }
    }).catch(() => {});
    check();
    // pagina Licență anunță activarea/sincronizarea, ca alerta să dispară imediat
    window.addEventListener("license-status-changed", check);
    return () => window.removeEventListener("license-status-changed", check);
  }, [isAdmin, activationRequired]);

  const doLogout = async () => { await logout(); nav("/login"); };
  const roleLabel = (user?.role_names || []).map(r => ({ admin: "Admin", hr: "HR", manager: "Manager", employee: "Angajat", superadmin: "Superadmin" })[r]).join(" · ") || "—";
  const groups = visibleGroups({ hasRole, hasModule, allowed, activationRequired, isClientInstall, theme: "purple" });
  return { user, nav, unread, platformVersion, pwa, iosHelp, setIosHelp, newBuild, licenseAlert, updateBanner, doLogout, roleLabel, groups };
}
